import { useState, useEffect, useCallback } from 'react';
import { getVideoElementFromMux } from '../../utils/media-utils';

// Detect if we're on iOS Safari
const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  return isIOS && isSafari;
};

// Detect if we're on a mobile device
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};

export const useFullscreenControl = (containerRef: React.RefObject<HTMLMediaElement>) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInFullscreen, setIsInFullscreen] = useState(false);
  const [shouldRestoreFullscreen, setShouldRestoreFullscreen] = useState(false);
  const [isIOSFullscreen, setIsIOSFullscreen] = useState(false); // Track iOS fullscreen state

  // Handle document fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = document.fullscreenElement !== null;
      setIsFullscreen(isCurrentlyFullscreen);
      setIsInFullscreen(isCurrentlyFullscreen);

      // Reset iOS fullscreen if we're in actual fullscreen
      if (isCurrentlyFullscreen) {
        setIsIOSFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);
  const exitIOSFullscreen = useCallback(() => {
    if (!containerRef.current) return false;

    try {
      const container = containerRef.current;
      const originalStyles = (container as any).__originalStyles;

      // Remove iOS fullscreen class
      container.classList.remove('ios-fullscreen');
      document.body.classList.remove('ios-fullscreen-body');

      // Restore original styles
      if (originalStyles) {
        Object.assign(container.style, originalStyles);
        delete (container as any).__originalStyles;
      }

      setIsIOSFullscreen(false);
      setIsInFullscreen(false);
      return true;
    } catch (error) {
      console.error('Error exiting iOS fullscreen:', error);
      return false;
    }
  }, [containerRef]);
  // Detect orientation change on iOS to exit pseudo-fullscreen
  useEffect(() => {
    if (!isIOSSafari()) return;

    const handleOrientationChange = () => {
      // Small delay to ensure orientation change is complete
      setTimeout(() => {
        if (isIOSFullscreen) {
          exitIOSFullscreen();
        }
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [isIOSFullscreen]);

  // iOS Fullscreen - Create a pseudo-fullscreen experience
  const enterIOSFullscreen = useCallback(() => {
    if (!containerRef.current) return false;

    try {
      const container = containerRef.current;

      // Add iOS fullscreen class
      container.classList.add('ios-fullscreen');
      document.body.classList.add('ios-fullscreen-body');

      // Hide address bar by scrolling
      window.scrollTo(0, 1);

      // Set container styles for fullscreen-like experience
      const originalStyles = {
        position: container.style.position,
        top: container.style.top,
        left: container.style.left,
        width: container.style.width,
        height: container.style.height,
        zIndex: container.style.zIndex,
        backgroundColor: container.style.backgroundColor,
      };

      // Store original styles
      (container as any).__originalStyles = originalStyles;

      // Apply fullscreen styles
      Object.assign(container.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        zIndex: '9999',
        backgroundColor: 'black',
      });

      setIsIOSFullscreen(true);
      setIsInFullscreen(true);
      return true;
    } catch (error) {
      console.error('Error entering iOS fullscreen:', error);
      return false;
    }
  }, [containerRef]);

  // Exit iOS fullscreen

  // Check if fullscreen is supported
  const isFullscreenSupported = useCallback(() => {
    if (!containerRef.current) return false;

    return !!(
      containerRef.current.requestFullscreen ||
      (containerRef.current as any).webkitRequestFullscreen ||
      (containerRef.current as any).mozRequestFullScreen ||
      (containerRef.current as any).msRequestFullscreen
    );
  }, [containerRef]);

  // Toggle fullscreen with fallbacks
  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    // 1. Handle exiting fullscreen if already in any fullscreen mode
    if (isIOSFullscreen) {
      // Currently in iOS pseudo-fullscreen
      exitIOSFullscreen();
      return;
    }
    if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
      // Currently in standard/browser fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((err) => console.error('Error exiting fullscreen:', err));
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen(); // For Safari and older WebKit
      }
      return;
    }

    // 2. Handle entering fullscreen
    const videoElement = getVideoElementFromMux();
    const isMobile = isMobileDevice();

    if (isMobile) {
      // --- On Mobile Devices: Prioritize native video player fullscreen ---
      let nativeVideoFullscreenInitiated = false;

      if (videoElement) {
        // Attempt 1: WebKit specific for iOS and some Android browsers (synchronous call)
        if (typeof (videoElement as any).webkitEnterFullscreen === 'function') {
          try {
            (videoElement as any).webkitEnterFullscreen();
            nativeVideoFullscreenInitiated = true;
            // Assuming browser handles UI and state updates correctly for this proprietary API
            return; // Exit after successful initiation
          } catch (error) {
            console.warn('Video webkitEnterFullscreen failed:', error);
          }
        }

        // Attempt 2: Standard requestFullscreen on the video element itself (asynchronous)
        if (
          !nativeVideoFullscreenInitiated &&
          typeof videoElement.requestFullscreen === 'function'
        ) {
          videoElement
            .requestFullscreen()
            .then(() => {
              // Successfully entered fullscreen via video.requestFullscreen
              // State updates (isFullscreen, isInFullscreen) handled by document event listeners
            })
            .catch((err) => {
              console.warn('Video element requestFullscreen failed:', err);
              // Fallback for specific scenarios if video.requestFullscreen fails
              if (isIOSSafari()) {
                enterIOSFullscreen(); // Fallback to pseudo-fullscreen for iOS
              } else if (isFullscreenSupported() && containerRef.current) {
                // Fallback to container fullscreen for other mobile devices
                const el = containerRef.current;
                if (el.requestFullscreen)
                  el.requestFullscreen().catch((e) =>
                    console.warn('Container FS fallback failed (mobile):', e),
                  );
                else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
                else if ((el as any).mozRequestFullScreen) (el as any).mozRequestFullScreen();
                else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();
              }
            });
          return; // Exit as requestFullscreen is async and manages its own flow/fallbacks
        }
      }

      // Fallback for Mobile if no videoElement or native video methods were not available/successful
      // This block is reached if:
      // - No videoElement
      // - videoElement exists, but webkitEnterFullscreen was not available or failed, AND video.requestFullscreen was not available.
      if (!nativeVideoFullscreenInitiated) {
        // Check if any native attempt was made for video element
        if (isIOSSafari()) {
          enterIOSFullscreen(); // iOS specific pseudo-fullscreen
        } else if (isFullscreenSupported() && containerRef.current) {
          // General mobile container fallback
          const el = containerRef.current;
          if (el.requestFullscreen)
            el.requestFullscreen().catch((e) =>
              console.warn('Container FS general fallback failed (mobile):', e),
            );
          else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
          else if ((el as any).mozRequestFullScreen) (el as any).mozRequestFullScreen();
          else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();
        } else {
          console.warn('No fullscreen method available or supported on this mobile device.');
        }
      }
    } else {
      // --- On Desktop Devices: Use custom player with container fullscreen API ---
      if (isFullscreenSupported()) {
        // isFullscreenSupported checks containerRef.current
        const element = containerRef.current;
        if (element.requestFullscreen) {
          element.requestFullscreen().catch((err) => {
            console.warn('Desktop container requestFullscreen failed:', err);
            // No iOS pseudo-fullscreen fallback for desktop.
          });
        } else if ((element as any).webkitRequestFullscreen) {
          (element as any).webkitRequestFullscreen();
        } else if ((element as any).mozRequestFullScreen) {
          (element as any).mozRequestFullScreen();
        } else if ((element as any).msRequestFullscreen) {
          (element as any).msRequestFullscreen();
        }
      } else {
        console.warn('Fullscreen not supported on this desktop browser for the custom player.');
      }
    }
  }, [
    containerRef,
    isIOSFullscreen,
    isFullscreenSupported,
    enterIOSFullscreen,
    exitIOSFullscreen,
    // getVideoElementFromMux, isMobileDevice, isIOSSafari are external or pure and don't need to be deps
  ]);

  // Custom fullscreen handler with class
  const handleCustomFullscreen = useCallback(() => {
    // Use the same logic as handleFullscreen
    handleFullscreen();
  }, [handleFullscreen]);

  // Exit fullscreen temporarily
  const exitFullscreenTemporarily = useCallback(async () => {
    if (isIOSFullscreen) {
      setShouldRestoreFullscreen(true);
      exitIOSFullscreen();
      return true;
    }

    if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
      setShouldRestoreFullscreen(true);

      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        }
        setIsInFullscreen(false);
        return true;
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
        return false;
      }
    }
    return false;
  }, [isIOSFullscreen, exitIOSFullscreen]);

  // Restore fullscreen
  const restoreFullscreen = useCallback(async () => {
    if (!shouldRestoreFullscreen || !containerRef.current) return false;

    if (isIOSSafari() && !isFullscreenSupported()) {
      enterIOSFullscreen();
      setShouldRestoreFullscreen(false);
      return true;
    }

    try {
      const element = containerRef.current;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      }
      setIsInFullscreen(true);
      setShouldRestoreFullscreen(false);
      return true;
    } catch (err) {
      console.error('Error restoring fullscreen:', err);
      setShouldRestoreFullscreen(false);
      return false;
    }
  }, [containerRef, shouldRestoreFullscreen, isFullscreenSupported, enterIOSFullscreen]);

  return {
    isFullscreen: isFullscreen || isIOSFullscreen,
    isInFullscreen: isInFullscreen || isIOSFullscreen,
    shouldRestoreFullscreen,
    isIOSFullscreen,
    setIsInFullscreen,
    setShouldRestoreFullscreen,
    handleFullscreen,
    handleCustomFullscreen,
    exitFullscreenTemporarily,
    restoreFullscreen,
    isFullscreenSupported,
  };
};
